import React, { useEffect, useState } from 'react';
import { agentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bot, Play, Clock, CheckCircle, XCircle, Download, 
  Package, TrendingUp, BarChart3, Users, Store,
  Star, ChevronRight, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';

// Domain color configurations (enterprise palette)
const DOMAIN_STYLES = {
  Inventory: {
    bg: 'bg-[#F1F5F9]',
    badgeBg: 'bg-slate-600',
    badgeText: 'text-white',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
    icon: Package
  },
  Sales: {
    bg: 'bg-[#EEF3FF]',
    badgeBg: 'bg-blue-600',
    badgeText: 'text-white',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    icon: TrendingUp
  },
  Demand: {
    bg: 'bg-[#F3EEFF]',
    badgeBg: 'bg-purple-600',
    badgeText: 'text-white',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    icon: BarChart3
  },
  Customer: {
    bg: 'bg-[#FFFBEB]',
    badgeBg: 'bg-amber-600',
    badgeText: 'text-white',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    icon: Users
  },
  Operations: {
    bg: 'bg-[#FFF3E8]',
    badgeBg: 'bg-orange-600',
    badgeText: 'text-white',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    icon: Store
  }
};

// Agent role subtitles
const AGENT_ROLES = {
  ReasonExecutiveBrain: 'Executive Intelligence Orchestrator',
  ReasonSalesIQ: 'Revenue & Performance Cockpit',
  ReasonStockSense: 'Inventory Intelligence',
  ReasonProductMix: 'Assortment & Category Intelligence',
  ReasonDemandFlow: 'Demand Forecasting',
  ReasonPromoIQ: 'Promotion Effectiveness',
  ReasonCustomer360: 'Customer Intelligence & CRM',
  ReasonReturnIQ: 'Returns Risk & Quality',
  ReasonStorePulse: 'Store Execution Intelligence',
  ReasonVendorLink: 'Vendor Scorecard & Risk'
};

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAgent, setRunningAgent] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [agentsRes, runsRes] = await Promise.all([
        agentAPI.listAgents(),
        agentAPI.getRuns({ page: 1, page_size: 10 })
      ]);
      setAgents(agentsRes.data.agents);
      setRuns(runsRes.data.runs);
    } catch (error) {
      toast.error('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (runId, agentName) => {
    try {
      toast.loading(`Generating report for ${agentName}...`);
      
      const response = await agentAPI.downloadReport(runId);
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentType = response.headers['content-type'];
      const extension = contentType.includes('pdf') ? 'pdf' : 'xlsx';
      link.download = `${agentName}_${runId}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Report downloaded successfully!');
      
    } catch (error) {
      toast.dismiss();
      const errorMessage = error.response?.data?.detail || error.message || 'Download failed';
      toast.error(`Failed to download report: ${errorMessage}`);
      console.error('Download error:', error);
    }
  };

  const runAgent = async (agentName) => {
    setRunningAgent(agentName);
    const loadingToast = toast.loading(`Running ${agentName}...`);
    
    try {
      const response = await agentAPI.runAgent(agentName);
      toast.dismiss(loadingToast);
      
      toast.success(
        <div className="bg-white dark:bg-slate-800 p-2 rounded">
          <div className="font-semibold text-slate-900 dark:text-white">{agentName} completed successfully!</div>
          <div className="text-sm mt-1 text-slate-600 dark:text-slate-300">
            {response.data.insightsCount} insights, {response.data.recommendationsCount} recommendations
          </div>
        </div>,
        { duration: 5000 }
      );
      
      setTimeout(() => loadData(), 1500);
      
    } catch (error) {
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      
      toast.error(
        <div className="bg-white dark:bg-slate-800 p-2 rounded">
          <div className="font-semibold text-slate-900 dark:text-white">Failed to run {agentName}</div>
          <div className="text-sm mt-1 text-slate-600 dark:text-slate-300">{errorMessage}</div>
        </div>,
        { duration: 7000 }
      );
      
      console.error('Agent execution error:', {
        agentName,
        error: error.response?.data || error.message,
        status: error.response?.status
      });
      
    } finally {
      setRunningAgent(null);
    }
  };

  // Domain categories for filtering
  const categories = ['All', 'Inventory', 'Sales', 'Demand', 'Customer', 'Operations'];
  
  // STRICT domain filtering using primary_domain only
  const filteredAgents = (() => {
    if (activeCategory === 'All') {
      // For "All" tab, sort with ExecutiveBrain pinned at top
      return [...agents].sort((a, b) => {
        if (a.is_executive) return -1;
        if (b.is_executive) return 1;
        return (a.priority || 99) - (b.priority || 99);
      });
    }
    
    // STRICT filter by primary_domain only - no inference
    return agents.filter(agent => {
      if (!agent.primary_domain) {
        console.warn(`Agent ${agent.name} has no primary_domain - will not appear in domain tabs`);
        return false;
      }
      return agent.primary_domain === activeCategory;
    }).sort((a, b) => {
      if (a.is_executive) return -1;
      if (b.is_executive) return 1;
      return (a.priority || 99) - (b.priority || 99);
    });
  })();

  // Get last run for each agent
  const getLastRun = (agentName) => {
    return runs.find(r => r.agentName === agentName);
  };

  // Get domain style
  const getDomainStyle = (domain) => {
    return DOMAIN_STYLES[domain] || DOMAIN_STYLES.Operations;
  };

  // Get agent display name (without "Reason" prefix)
  const getDisplayName = (agentName) => {
    return agentName.replace('Reason', '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20" data-testid="agents-page">
      <div className="fade-in">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">AI Agents Console</h1>
        <p className="text-slate-600 dark:text-slate-400">Execute and monitor AI-powered retail intelligence agents</p>
      </div>

      {/* Domain Category Filters - Enterprise styled tabs */}
      <div className="flex flex-wrap gap-2 fade-in">
        {categories.map((cat) => {
          const style = cat === 'All' ? null : getDomainStyle(cat);
          const agentCount = cat === 'All' 
            ? agents.length 
            : agents.filter(a => a.primary_domain === cat).length;
          
          return (
            <Button
              key={cat}
              data-testid={`filter-${cat.toLowerCase()}`}
              onClick={() => setActiveCategory(cat)}
              variant={activeCategory === cat ? 'default' : 'outline'}
              className={`
                ${activeCategory === cat 
                  ? (cat === 'All' ? 'bg-slate-800 hover:bg-slate-900' : style?.badgeBg + ' hover:opacity-90')
                  : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                }
                transition-all duration-200
              `}
            >
              {cat}
              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                activeCategory === cat ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'
              }`}>
                {agentCount}
              </span>
            </Button>
          );
        })}
      </div>

      {/* Agent Cards - Premium Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 fade-in">
        {filteredAgents.map((agent, index) => {
          const lastRun = getLastRun(agent.name);
          const domainStyle = getDomainStyle(agent.primary_domain);
          const DomainIcon = domainStyle.icon;
          const isExecutive = agent.is_executive;
          
          return (
            <Card 
              key={index} 
              className={`
                border-0 shadow-lg overflow-hidden
                ${isExecutive ? 'ring-2 ring-amber-400 shadow-amber-100' : ''}
                transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5
              `}
              data-testid={`agent-card-${agent.name}`}
            >
              {/* Top accent bar */}
              <div className={`h-1.5 ${domainStyle.badgeBg}`}></div>
              
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 ${domainStyle.iconBg} rounded-xl`}>
                      <DomainIcon className={`h-6 w-6 ${domainStyle.iconColor}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{getDisplayName(agent.name)}</CardTitle>
                        {isExecutive && (
                          <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 border-0 text-[10px] px-1.5 py-0.5">
                            <Star className="h-3 w-3 mr-0.5" fill="currentColor" />
                            Executive
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {AGENT_ROLES[agent.name] || agent.primary_domain}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Domain Badge */}
                <div className="mt-3 flex items-center gap-2">
                  <Badge className={`${domainStyle.badgeBg} ${domainStyle.badgeText} text-[10px] px-2`}>
                    {agent.primary_domain}
                  </Badge>
                  {agent.priority_scope && (
                    <span className="text-[10px] text-slate-400">Focus: {agent.priority_scope}</span>
                  )}
                </div>
                
                <CardDescription className="mt-3 text-sm leading-relaxed line-clamp-2">
                  {agent.description}
                </CardDescription>
                
                {/* Business Roles (subtle) */}
                {agent.business_roles && agent.business_roles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {agent.business_roles.slice(0, 3).map((role, idx) => (
                      <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">
                        {role}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Last Run Status */}
                {lastRun && (
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {lastRun.status === 'completed' || lastRun.status === 'SUCCESS' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : lastRun.status === 'failed' ? (
                          <XCircle className="h-4 w-4 text-red-500" />
                        ) : (
                          <Clock className="h-4 w-4 text-yellow-500 animate-spin" />
                        )}
                        <span className="text-xs text-slate-500">Last run: {formatDateTime(lastRun.startedAt)}</span>
                      </div>
                      {(lastRun.status === 'completed' || lastRun.status === 'SUCCESS') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadReport(lastRun.runId, agent.name)}
                          className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Report
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="pt-0">
                <Button
                  data-testid={`run-agent-${agent.name}`}
                  onClick={() => runAgent(agent.name)}
                  disabled={runningAgent === agent.name}
                  className={`
                    w-full transition-all duration-200
                    ${isExecutive 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' 
                      : `${domainStyle.badgeBg} hover:opacity-90 text-white`
                    }
                  `}
                >
                  {runningAgent === agent.name ? (
                    <><Clock className="h-4 w-4 mr-2 animate-spin" /> Running...</>
                  ) : (
                    <><Play className="h-4 w-4 mr-2" /> Run Agent</>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredAgents.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg">No agents found in {activeCategory} domain</p>
          <p className="text-sm mt-1">Try selecting a different category</p>
        </div>
      )}

      {/* Recent Runs Table */}
      <Card className="border-0 shadow-lg fade-in">
        <Tabs defaultValue="runs">
          <CardHeader>
            <CardTitle className="text-lg">Recent Agent Runs</CardTitle>
            <TabsList className="grid w-full max-w-md grid-cols-1 mt-2">
              <TabsTrigger value="runs" data-testid="tab-runs">Execution History</TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent>
            <TabsContent value="runs" className="space-y-4 mt-0">
              {runs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bot className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No agent runs yet</p>
                  <p className="text-sm mt-1">Run an agent to see execution history</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {runs.map((run, index) => {
                    const agent = agents.find(a => a.name === run.agentName);
                    const domainStyle = agent ? getDomainStyle(agent.primary_domain) : getDomainStyle('Operations');
                    
                    return (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        data-testid={`run-item-${index}`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-2 rounded-lg ${domainStyle.iconBg}`}>
                            <Bot className={`h-5 w-5 ${domainStyle.iconColor}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 dark:text-white">
                                {getDisplayName(run.agentName)}
                              </p>
                              {agent && (
                                <Badge className={`${domainStyle.badgeBg} ${domainStyle.badgeText} text-[9px] px-1.5`}>
                                  {agent.primary_domain}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400">{formatDateTime(run.startedAt)}</p>
                            {run.summary && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-1">{run.summary}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {run.status === 'completed' || run.status === 'SUCCESS' ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                <span className="text-sm font-medium text-green-600">Completed</span>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadReport(run.runId, run.agentName)}
                                className="ml-2"
                                title="Download Report"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </>
                          ) : run.status === 'failed' ? (
                            <div className="flex items-center gap-1.5">
                              <XCircle className="h-5 w-5 text-red-500" />
                              <span className="text-sm font-medium text-red-600">Failed</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-5 w-5 text-yellow-500 animate-spin" />
                              <span className="text-sm font-medium text-yellow-600">Running</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Agents;
